import React, { useContext, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight } from 'lucide-react';
import { axios, auth, mbUrl, MbContext, muted, primary, runCall } from './mbApi';

export const STAGE_LABEL = { brief: 'Brief', creative: 'Bahan / Creative', test: 'Ujian / Media Buying', review: 'Nilai Result' };
const NEXT = { brief: 'creative', creative: 'test', test: 'review', review: 'creative' };

export const usePicName = () => {
  const { members } = useContext(MbContext);
  return (id) => members.find((m) => m.id === id)?.name || '—';
};

const TestGuard = ({ w, eligible }) => (
  <p className={`text-[11px] ${eligible.length ? 'text-emerald-400' : 'text-amber-400'}`} data-testid={`mb-handoff-guard-${w.id}`}>
    {eligible.length
      ? `Versi layak diuji: ${eligible.map((v) => v.version_label).join(', ')}. Hanya versi Lulus boleh guna Ujian Baru.`
      : 'Belum boleh serah ke Ujian: perlu satu versi (bukan arkib) yang ada link bahan https://… dan review Lulus.'}
  </p>
);

export const HandoffControl = ({ ibId, w, canHandoff, eligible = [], reload }) => {
  const cur = w.current_stage || 'brief';
  const [to, setTo] = useState(NEXT[cur]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const picName = usePicName();
  useEffect(() => { setTo(NEXT[cur]); setNote(''); }, [cur, w.id]);
  const invalid = !STAGE_LABEL[to] || to === cur || (to === 'test' && !eligible.length);
  const send = () => runCall(setBusy, async () => {
    await axios.post(mbUrl(ibId, `/workflows/${w.id}/handoff`), { to_stage: to, note }, auth());
    setNote('');
    reload();
  }, 'Diserahkan ke PIC seterusnya');

  return (
    <div className="space-y-1.5" data-testid={`mb-handoff-${w.id}`}>
      <p className={`text-xs ${muted}`}>
        Tahap semasa: <span className={primary} data-testid={`mb-current-stage-${w.id}`}>{STAGE_LABEL[cur]}</span>
        {' · '}PIC: <span className={primary} data-testid={`mb-current-pic-${w.id}`}>{picName(w.current_pic_id)}</span>
      </p>
      {canHandoff && !w.archived && (
        <>
          <div className="flex flex-wrap gap-1.5">
            <Select value={to} onValueChange={setTo}>
              <SelectTrigger className="h-10 w-44 text-xs" data-testid={`mb-handoff-to-${w.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(STAGE_LABEL).filter(([k]) => k !== cur).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
            <Input className="h-10 text-xs w-56" placeholder="Nota serahan (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`mb-handoff-note-${w.id}`} />
            <Button size="sm" className="h-10 text-xs" disabled={busy || invalid} onClick={send} data-testid={`mb-handoff-send-${w.id}`}>
              Serah <ArrowRight size={12} className="ml-1" />
            </Button>
          </div>
          {to === 'test' && <TestGuard w={w} eligible={eligible} />}
        </>
      )}
      {(w.handoffs || []).length > 0 && (
        <details className={`text-[11px] ${muted}`}>
          <summary className="cursor-pointer" data-testid={`mb-handoff-history-${w.id}`}>Sejarah serahan ({w.handoffs.length})</summary>
          {[...w.handoffs].reverse().map((h, i) => (
            <p key={`${h.at}-${i}`}>{new Date(h.at).toLocaleString('en-GB')} · {h.by_name}: {STAGE_LABEL[h.from_stage]} → {STAGE_LABEL[h.to_stage]} ({picName(h.to_pic_id)}){h.note ? ` — "${h.note}"` : ''}</p>
          ))}
        </details>
      )}
    </div>
  );
};
